import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ftIn } from '../utils/formatMeasure';

function ProjectForm() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const isEditing = state?.isEditing || false;
  const projectId = state?.projectId || null;
  const extracted = state?.extractedData || {};
  const savedForm = state?.form || {};
  const projectName = state?.projectName || '';
  const fileName = state?.fileName || '';

  const [form, setForm] = useState(isEditing ? {
    structureType: savedForm.structureType || '',
    totalWidth: savedForm.totalWidth || '',
    totalLength: savedForm.totalLength || '',
    height: savedForm.height || '',
    roofSlope: savedForm.roofSlope || '',
    rafterSpacing: savedForm.rafterSpacing || '',
    longStrips: savedForm.longStrips || '',
    shortStrips: savedForm.shortStrips || '',
    longLength: savedForm.longLength || '',
    shortLength: savedForm.shortLength || '',
    mainBeamProfile: savedForm.mainBeamProfile || '',
    rafterProfile: savedForm.rafterProfile || '',
    columnType: savedForm.columnType || '',
    numberOfColumns: savedForm.numberOfColumns || '',
    columnCutType: savedForm.columnCutType || 'straight',
    columnInsertSize: savedForm.columnInsertSize || 'auto',
    numberOfInserts: savedForm.numberOfInserts || '',
    screenType: savedForm.screenType || '',
    numberOfDoors: savedForm.numberOfDoors || '',
    doorSize: savedForm.doorSize || '',
    kickPlate: savedForm.kickPlate || '',
    fanBeam: savedForm.fanBeam || '',
    egutterType: savedForm.egutterType || '',
    stepBalance: savedForm.stepBalance || '',
    header: savedForm.header || '',
    mansard: savedForm.mansard || { rise: '', width: '', length: '', carrierBeamProfile: '', purlinProfile: '', rafterProfile: '' },
    spanTable: savedForm.spanTable || null
  } : {
    structureType: extracted.structureType || '',
    totalWidth: extracted.dimensions?.totalWidth || '',
    totalLength: extracted.dimensions?.totalLength || '',
    height: extracted.dimensions?.height || '',
    roofSlope: extracted.dimensions?.roofSlope || '',
    rafterSpacing: extracted.dimensions?.rafterSpacing || '',
    longStrips: extracted.dimensions?.longStrips || '',
    shortStrips: extracted.dimensions?.shortStrips || '',
    longLength: extracted.dimensions?.longLength || '',
    shortLength: extracted.dimensions?.shortLength || '',
    mainBeamProfile: extracted.structuralProfiles?.mainBeamProfile || '',
    rafterProfile: extracted.structuralProfiles?.rafterProfile || '',
    columnType: extracted.structuralProfiles?.columnType || '',
    numberOfColumns: extracted.structuralProfiles?.numberOfColumns || '',
    columnCutType: 'straight',
    columnInsertSize: 'auto',
    numberOfInserts: '',
    screenType: '',
    numberOfDoors: extracted.doors?.numberOfDoors || '',
    doorSize: extracted.doors?.doorSize || '',
    kickPlate: extracted.kickPlate != null ? String(extracted.kickPlate) : '',
    fanBeam: '',
    egutterType: '',
    stepBalance: '',
    header: '',
    mansard: extracted.mansard || { rise: '', width: '', length: '', carrierBeamProfile: '', purlinProfile: '', rafterProfile: '' },
    spanTable: extracted.spanTable || null
  });

  const [touched, setTouched] = useState({});
  const touch = (key) => setTouched(prev => ({ ...prev, [key]: true }));

  const isExtracted = (key) => {
    const map = {
      totalWidth: extracted.dimensions?.totalWidth,
      totalLength: extracted.dimensions?.totalLength,
      height: extracted.dimensions?.height,
      roofSlope: extracted.dimensions?.roofSlope,
      mainBeamProfile: extracted.structuralProfiles?.mainBeamProfile,
      rafterProfile: extracted.structuralProfiles?.rafterProfile,
      columnType: extracted.structuralProfiles?.columnType,
      numberOfColumns: extracted.structuralProfiles?.numberOfColumns,
      numberOfDoors: extracted.doors?.numberOfDoors,
      doorSize: extracted.doors?.doorSize,
      kickPlate: extracted.kickPlate,
      longStrips: extracted.dimensions?.longStrips,
      shortStrips: extracted.dimensions?.shortStrips,
      longLength: extracted.dimensions?.longLength,
      shortLength: extracted.dimensions?.shortLength,
    };
    return map[key] !== null && map[key] !== undefined;
  };

  const requiredFields = [
    'structureType', 'totalWidth', 'totalLength', 'height', 'roofSlope',
    'mainBeamProfile', 'rafterProfile', 'columnType', 'numberOfColumns',
    'numberOfDoors', 'doorSize', 'kickPlate',
  ];
  const missingFields = requiredFields.filter(k => !form[k]);
  const extractedCount = Object.keys(form).filter(k => isExtracted(k) && form[k]).length;

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const handleMansardChange = (key, value) => setForm(prev => ({ ...prev, mansard: { ...prev.mansard, [key]: value } }));

  const handleNext = () => {
    const allTouched = Object.fromEntries(requiredFields.map(k => [k, true]));
    setTouched(prev => ({ ...prev, ...allTouched }));
    if (missingFields.length > 0) return alert('Please complete all required fields.');
    navigate('/review', { state: { form, projectName, fileName, isEditing, projectId } });
  };

  const structureTypes = ['screen_enclosure', 'screen_porch', 'mixed', 'mansard'];
  const structureLabels = { screen_enclosure: 'Screen Enclosure', screen_porch: 'Screen Porch', mixed: 'Mixed Structure', mansard: 'Mansard' };

  // Returns true when a required field should be highlighted as invalid
  const isInvalid = (key) => touched[key] && !form[key];

  return (
    <div>
      <div className="page-header">
        <div className="breadcrumb"><a href="/">Home</a> / {isEditing ? 'Edit Project' : 'New Project'} / <strong>Form</strong></div>
        <h1 className="page-title">Project Details</h1>
        <p className="page-subtitle">{isEditing ? 'Edit saved project' : 'Review extracted data and complete missing fields'}</p>
      </div>

      <div className="info-banner">
        {isEditing
          ? '✏️ Editing saved project — modify any field and recalculate'
          : `ℹ️ We extracted ${extractedCount} fields from your plan. Please complete the ${missingFields.length} missing fields.`}
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '12px' }}>Structure Type</h3>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '8px' }}>
          {structureTypes.map(type => (
            <button
              key={type}
              onClick={() => { handleChange('structureType', type); touch('structureType'); }}
              style={{
                flex: 1, padding: '10px', borderRadius: '6px', cursor: 'pointer', fontWeight: '500',
                border: form.structureType === type ? '2px solid #1a73e8' : '1px solid #e0e0e0',
                backgroundColor: form.structureType === type ? '#e8f0fe' : 'white',
                color: form.structureType === type ? '#1a73e8' : '#333'
              }}
            >
              {structureLabels[type]}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '16px' }}>General Dimensions</h3>
        <div className="form-grid">
          {[['totalWidth', 'Total Width', 'ft'], ['totalLength', 'Total Length', 'ft'], ['height', 'Height', 'ft'], ['roofSlope', 'Roof Slope', '']].map(([key, label, unit]) => (
            <div key={key} className="form-group">
              <label className="form-label">
                {label} {!isExtracted(key) && isInvalid(key) && <span style={{ color: '#d93025', fontSize: '12px' }}>required</span>}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  className={`form-input ${isExtracted(key) ? 'extracted' : isInvalid(key) ? 'missing' : ''}`}
                  value={form[key]}
                  onChange={e => handleChange(key, e.target.value)}
                  onBlur={() => touch(key)}
                  placeholder={unit}
                />
                {isExtracted(key) && <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#34a853' }}>✓</span>}
              </div>
              {unit === 'ft' && form[key] && !isNaN(parseFloat(form[key])) && (
                <span style={{ fontSize: '11px', color: '#888', marginTop: '3px', display: 'block' }}>= {ftIn(form[key])}</span>
              )}
            </div>
          ))}
          <div className="form-group">
            <label className="form-label">Rafter Spacing
              {extracted.dimensions?.rafterSpacing != null && (
                <span style={{ color: '#34a853', fontSize: '11px', marginLeft: '6px' }}>✓ from plan</span>
              )}
            </label>
            {extracted.dimensions?.rafterSpacing != null ? (
              <input
                className="form-input extracted"
                readOnly
                value={ftIn(form.rafterSpacing)}
                style={{ background: '#f5f5f5', cursor: 'default' }}
              />
            ) : (
              <>
                <input
                  className="form-input"
                  value={form.rafterSpacing}
                  onChange={e => handleChange('rafterSpacing', e.target.value)}
                  placeholder="Enter rafter spacing (ft)"
                />
                {form.rafterSpacing && !isNaN(parseFloat(form.rafterSpacing)) && (
                  <span style={{ fontSize: '11px', color: '#888', marginTop: '3px', display: 'block' }}>= {ftIn(form.rafterSpacing)}</span>
                )}
              </>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Long Strips
              {isExtracted('longStrips') && <span style={{ color: '#34a853', fontSize: '11px', marginLeft: '6px' }}>✓ from plan</span>}
            </label>
            {isExtracted('longStrips') ? (
              <input className="form-input extracted" readOnly value={form.longStrips} style={{ background: '#f5f5f5', cursor: 'default' }} />
            ) : (
              <input className="form-input" type="number" min="0" value={form.longStrips} onChange={e => handleChange('longStrips', e.target.value)} placeholder="number of strips" />
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Long Length (ft)
              {isExtracted('longLength') && <span style={{ color: '#34a853', fontSize: '11px', marginLeft: '6px' }}>✓ from plan</span>}
            </label>
            {isExtracted('longLength') ? (
              <input className="form-input extracted" readOnly value={ftIn(form.longLength)} style={{ background: '#f5f5f5', cursor: 'default' }} />
            ) : (
              <>
                <input className="form-input" value={form.longLength} onChange={e => handleChange('longLength', e.target.value)} placeholder="ft" />
                {form.longLength && !isNaN(parseFloat(form.longLength)) && (
                  <span style={{ fontSize: '11px', color: '#888', marginTop: '3px', display: 'block' }}>= {ftIn(form.longLength)}</span>
                )}
              </>
            )}
          </div>
          <div className="form-group">
            <label className="form-label">Short Strips
              {isExtracted('shortStrips') && <span style={{ color: '#34a853', fontSize: '11px', marginLeft: '6px' }}>✓ from plan</span>}
            </label>
            {isExtracted('shortStrips') ? (
              <input className="form-input extracted" readOnly value={form.shortStrips} style={{ background: '#f5f5f5', cursor: 'default' }} />
            ) : (
              <input className="form-input" type="number" min="0" value={form.shortStrips} onChange={e => handleChange('shortStrips', e.target.value)} placeholder="0 if rectangular roof" />
            )}
          </div>
          {(parseInt(form.shortStrips) > 0 || isExtracted('shortLength')) && (
            <div className="form-group">
              <label className="form-label">Short Length (ft)
                {isExtracted('shortLength') && <span style={{ color: '#34a853', fontSize: '11px', marginLeft: '6px' }}>✓ from plan</span>}
              </label>
              {isExtracted('shortLength') ? (
                <input className="form-input extracted" readOnly value={ftIn(form.shortLength)} style={{ background: '#f5f5f5', cursor: 'default' }} />
              ) : (
                <>
                  <input className="form-input" value={form.shortLength} onChange={e => handleChange('shortLength', e.target.value)} placeholder="ft" />
                  {form.shortLength && !isNaN(parseFloat(form.shortLength)) && (
                    <span style={{ fontSize: '11px', color: '#888', marginTop: '3px', display: 'block' }}>= {ftIn(form.shortLength)}</span>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '16px' }}>Structural Profiles</h3>
        <div className="form-grid">
          {[['mainBeamProfile', 'Main Beam Profile'], ['rafterProfile', 'Rafter Profile'], ['columnType', 'Column Type'], ['numberOfColumns', 'Number of Columns']].map(([key, label]) => (
            <div key={key} className="form-group">
              <label className="form-label">
                {label} {!isExtracted(key) && isInvalid(key) && <span style={{ color: '#d93025', fontSize: '12px' }}>required</span>}
              </label>
              <div style={{ position: 'relative' }}>
                <input
                  className={`form-input ${isExtracted(key) ? 'extracted' : isInvalid(key) ? 'missing' : ''}`}
                  value={form[key]}
                  onChange={e => handleChange(key, e.target.value)}
                  onBlur={() => touch(key)}
                />
                {isExtracted(key) && <span style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', color: '#34a853' }}>✓</span>}
              </div>
            </div>
          ))}
          <div className="form-group">
            <label className="form-label">Column Cut Type</label>
            <select className="form-input" value={form.columnCutType} onChange={e => handleChange('columnCutType', e.target.value)}>
              <option value="straight">Straight</option>
              <option value="diagonal">Diagonal</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Column Insert Size</label>
            <select className="form-input" value={form.columnInsertSize} onChange={e => handleChange('columnInsertSize', e.target.value)}>
              <option value="auto">Auto (same as column)</option>
              <option value="4">4 inches</option>
              <option value="6">6 inches</option>
              <option value="none">None</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Number of Inserts</label>
            <input
              className="form-input"
              type="number"
              min="0"
              value={form.numberOfInserts}
              onChange={e => handleChange('numberOfInserts', e.target.value)}
              placeholder={form.numberOfColumns ? `${form.numberOfColumns} (same as columns)` : 'Same as columns'}
            />
          </div>
        </div>
      </div>

      <div className="card">
        <h3 style={{ marginBottom: '16px' }}>Screen & Doors</h3>
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">
              Number of Doors {isInvalid('numberOfDoors') && <span style={{ color: '#d93025', fontSize: '12px' }}>required</span>}
              {isExtracted('numberOfDoors') && <span style={{ color: '#34a853', fontSize: '11px', marginLeft: '6px' }}>✓ from plan — verify</span>}
            </label>
            <input
              className={`form-input ${isInvalid('numberOfDoors') ? 'missing' : ''}`}
              value={form.numberOfDoors}
              onChange={e => handleChange('numberOfDoors', e.target.value)}
              onBlur={() => touch('numberOfDoors')}
            />
          </div>
          <div className="form-group">
            <label className="form-label">
              Door Size {isInvalid('doorSize') && <span style={{ color: '#d93025', fontSize: '12px' }}>required</span>}
              {isExtracted('doorSize') && <span style={{ color: '#34a853', fontSize: '11px', marginLeft: '6px' }}>✓ from plan — verify</span>}
            </label>
            <select
              className={`form-input ${isInvalid('doorSize') ? 'missing' : ''}`}
              value={form.doorSize}
              onChange={e => handleChange('doorSize', e.target.value)}
              onBlur={() => touch('doorSize')}
            >
              <option value="">Select size</option>
              <option value="36">36 inches</option>
              <option value="48">48 inches</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">
              Kick Plate {isInvalid('kickPlate') && <span style={{ color: '#d93025', fontSize: '12px' }}>required</span>}
              {isExtracted('kickPlate') && <span style={{ color: '#34a853', fontSize: '11px', marginLeft: '6px' }}>✓ from plan — verify</span>}
            </label>
            <select
              className={`form-input ${isInvalid('kickPlate') ? 'missing' : ''}`}
              value={form.kickPlate}
              onChange={e => handleChange('kickPlate', e.target.value)}
              onBlur={() => touch('kickPlate')}
            >
              <option value="">Select size</option>
              <option value="none">None — Sin Kick Plate</option>
              <option value="8">8 inches</option>
              <option value="16">16 inches</option>
            </select>
          </div>
        </div>
      </div>

      {(form.structureType === 'screen_porch' || form.structureType === 'mixed') && (() => {
        const width = parseFloat(form.totalWidth);
        const egutterCommercialSize = !isNaN(width) && width > 0
          ? (width <= 24 ? 24 : 30)
          : null;
        const egutterPieces = egutterCommercialSize
          ? Math.ceil(width / egutterCommercialSize)
          : null;
        return (
          <div className="card">
            <h3 style={{ marginBottom: '16px' }}>Insulated Roof</h3>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Fan Beam panels</label>
                <input className="form-input" type="number" min="0" value={form.fanBeam} onChange={e => handleChange('fanBeam', e.target.value)} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label">E-Gutter Type</label>
                <select className="form-input" value={form.egutterType} onChange={e => handleChange('egutterType', e.target.value)}>
                  <option value="">Select type</option>
                  <option value="3in">3 inches</option>
                  <option value="5in">5 inches</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">E-Gutter Length</label>
                <div className="form-input" style={{ background: '#f5f5f5', color: egutterCommercialSize ? '#333' : '#999', cursor: 'default' }}>
                  {egutterCommercialSize
                    ? `${egutterPieces}× ${egutterCommercialSize}ft bar${egutterPieces > 1 ? 's' : ''} (covers ${width}ft width)`
                    : 'Enter Total Width first'}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Step Balance / Fascia (pieces)</label>
                <input className="form-input" type="number" min="0" value={form.stepBalance} onChange={e => handleChange('stepBalance', e.target.value)} placeholder="0" />
              </div>
              <div className="form-group">
                <label className="form-label">Header (pieces)</label>
                <input className="form-input" type="number" min="0" value={form.header} onChange={e => handleChange('header', e.target.value)} placeholder="0" />
              </div>
            </div>
          </div>
        );
      })()}

      {form.structureType === 'mansard' && (
        <div className="card">
          <h3 style={{ marginBottom: '16px' }}>Mansard Roof</h3>
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Mansard Rise (ft)</label>
              <input className="form-input" type="number" min="0" value={form.mansard.rise} onChange={e => handleMansardChange('rise', e.target.value)} placeholder="ft" />
              {form.mansard.rise && !isNaN(parseFloat(form.mansard.rise)) && (
                <span style={{ fontSize: '11px', color: '#888', marginTop: '3px', display: 'block' }}>= {ftIn(form.mansard.rise)}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Mansard Width (ft)</label>
              <input className="form-input" type="number" min="0" value={form.mansard.width} onChange={e => handleMansardChange('width', e.target.value)} placeholder="ft" />
              {form.mansard.width && !isNaN(parseFloat(form.mansard.width)) && (
                <span style={{ fontSize: '11px', color: '#888', marginTop: '3px', display: 'block' }}>= {ftIn(form.mansard.width)}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Mansard Length (ft)</label>
              <input className="form-input" type="number" min="0" value={form.mansard.length} onChange={e => handleMansardChange('length', e.target.value)} placeholder="ft" />
              {form.mansard.length && !isNaN(parseFloat(form.mansard.length)) && (
                <span style={{ fontSize: '11px', color: '#888', marginTop: '3px', display: 'block' }}>= {ftIn(form.mansard.length)}</span>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">Carrier Beam Profile</label>
              <input className="form-input" value={form.mansard.carrierBeamProfile} onChange={e => handleMansardChange('carrierBeamProfile', e.target.value)} placeholder="e.g. 2X8 SMB" />
            </div>
            <div className="form-group">
              <label className="form-label">Purlin Profile</label>
              <input className="form-input" value={form.mansard.purlinProfile} onChange={e => handleMansardChange('purlinProfile', e.target.value)} placeholder="e.g. 2X2" />
            </div>
            <div className="form-group">
              <label className="form-label">Rafter Profile</label>
              <input className="form-input" value={form.mansard.rafterProfile} onChange={e => handleMansardChange('rafterProfile', e.target.value)} placeholder="e.g. 2X2" />
            </div>
          </div>
        </div>
      )}

      <div className="action-buttons">
        <button className="btn-secondary" onClick={() => navigate('/upload')}>Back</button>
        <button className="btn-primary" onClick={handleNext}>{isEditing ? 'Recalculate →' : 'Calculate Materials →'}</button>
      </div>
    </div>
  );
}

export default ProjectForm;
